import * as React from 'react';
import { useIntl } from 'react-intl';

import { DrawerContext, IconRegularClose } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import { getSupportedAttributes } from '@components/Tags/model';
import messages from '@containers/ProductDetailContainer/messages';
import { dataLabelsProp } from '@helpers/utils';

import { HandleTransitionClick } from '../Variants/useVariantTransition';
import {
  getContrastingIconColor,
  IVariantItemProps,
} from '../Variants/VariantsColorPickers/ColorPicker';
import { VariantLink } from '../variantsStyled';

import { OffsetScroll } from './components/OffsetScroll';
import { COLOR_CIRCLE_SIZE } from './constants';
import { ItemStatus } from './ItemStatus';
import {
  VariantListItem,
  Variant,
  BorderedColorCircle,
  AdditionalInfo,
  VariantListItemInfo,
  ColorCircleWrapper,
} from './styled';
import { trackDrawerVariantClick } from './tracking';
import { getItemColors } from './utils';

export interface ColorCirclePickerProps extends IVariantItemProps {
  getTimeFromInit: () => number;
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  onClick?: HandleTransitionClick;
}

export const ColorCirclePicker: React.FC<ColorCirclePickerProps> = ({
  item,
  isSelected,
  getTimeFromInit,
  product,
  onClick,
}) => {
  const { closeDrawer } = DrawerContext.useDrawerContext();

  const { formatMessage } = useIntl();
  const supportedAttributes = getSupportedAttributes();

  const isOutOfStock = item.stockAvailability.code === IStock.outOfStock;
  const colors = getItemColors({
    colors: (item.colors ?? []).filter((c): c is string => c != null),
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(item, e);
    trackDrawerVariantClick({ timing: getTimeFromInit() });
    closeDrawer();
  };

  const colorTileProps = {
    width: COLOR_CIRCLE_SIZE,
    height: COLOR_CIRCLE_SIZE,
    colors: colors,
  };

  return (
    <VariantListItem
      aria-label={isOutOfStock ? formatMessage(messages.outOfStock) : undefined}
      aria-disabled={isOutOfStock}
    >
      {isSelected && <OffsetScroll />}
      <ErrorBoundary>
        <VariantLink
          replace={true}
          to={item.url ?? ''}
          onClick={handleClick}
          id={`pd-variant-${item.webId ?? ''}`}
          data-testid={`pd-variant-${item.webId ?? ''}`}
          className={isSelected ? 'pd-variant-selected' : ''}
          {...dataLabelsProp({
            attributes: item.attributes,
            supportedAttributes,
          })}
        >
          <Variant>
            <VariantListItemInfo>
              <ColorCircleWrapper isSelected={isSelected}>
                <BorderedColorCircle {...colorTileProps}>
                  {isOutOfStock && (
                    <IconRegularClose
                      color={getContrastingIconColor(colors)}
                      width="20px"
                      height="20px"
                    />
                  )}
                </BorderedColorCircle>
              </ColorCircleWrapper>
              <AdditionalInfo
                isSelected={isSelected}
                isOutOfStock={isOutOfStock}
              >
                {item.additionalInfo}
              </AdditionalInfo>
            </VariantListItemInfo>

            <ItemStatus item={item} product={product} />
          </Variant>
        </VariantLink>
      </ErrorBoundary>
    </VariantListItem>
  );
};
